import { Vector2, PlaneGeometry } from "three";
import { extend } from "@react-three/fiber";
import { v4 as uuidv4 } from "uuid";
import ImageSlider from "./UI/Modals/ImageSlider/ImageSlider";
import { div } from "three/examples/jsm/nodes/math/OperatorNode";
import styles from "./UI/Modals/FlyingModal/FlyingModal.module.css";
import ImageSolo from "./UI/Modals/ImageSlider/ImageSolo";
import { useDispatch } from "react-redux";
import { setCurrentModule } from "./store/reducers/progressionReducer";
import { logEvent } from "./analytics";

// Paul West @prisoner849 https://discourse.threejs.org/u/prisoner849
// https://discourse.threejs.org/t/simple-curved-plane/26647/10
class BentPlaneGeometry extends PlaneGeometry {
  constructor(radius, ...args) {
    super(...args);
    let p = this.parameters;
    let hw = p.width * 0.5;
    let a = new Vector2(-hw, 0);
    let b = new Vector2(0, radius);
    let c = new Vector2(hw, 0);
    let ab = new Vector2().subVectors(a, b);
    let bc = new Vector2().subVectors(b, c);
    let ac = new Vector2().subVectors(a, c);
    let r =
      (ab.length() * bc.length() * ac.length()) / (2 * Math.abs(ab.cross(ac)));
    let center = new Vector2(0, radius - r);
    let baseV = new Vector2().subVectors(a, center);
    let baseAngle = baseV.angle() - Math.PI * 0.5;
    let arc = baseAngle * 2;
    let uv = this.attributes.uv;
    let pos = this.attributes.position;
    let mainV = new Vector2();
    for (let i = 0; i < uv.count; i++) {
      let uvRatio = 1 - uv.getX(i);
      let y = pos.getY(i);
      mainV.copy(c).rotateAround(center, arc * uvRatio);
      pos.setXYZ(i, mainV.x, y, -mainV.y);
    }
    pos.needsUpdate = true;
  }
}

export function easeInCubic(x) {
  return x * x;
}
export function easeOutCubic(x) {
  return 1 - Math.pow(1 - x, 3);
}

export function easeInOutCubic(x) {
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

export function easeInOutQuad(x) {
  return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
}

export function animInText(tl, words, words2 = null) {
  tl.fromTo(
    words,
    {
      willChange: "transform",
      transformOrigin: "0% 50%",
      y: "1.25em",
    },
    {
      duration: 0.3,
      ease: "power2.out",
      y: "0em",
      rotation: 0,
      stagger: {
        each: 0.025,
        from: "start",
      },
    },
  );
  if (words2) {
    tl.fromTo(
      words2,
      {
        willChange: "transform",
        transformOrigin: "0% 50%",
        y: "1.25em",
      },
      {
        duration: 0.3,
        ease: "power2.out",
        y: "0em",
        stagger: {
          each: 0.01,
          from: "start",
        },
      },
      "<-.05",
    );
  }
  return tl;
}

export function animOutText(tl, words, words2 = null) {
  tl.to(words, {
    duration: 1,
    ease: "power2.out",
    yPercent: 125,
    rotation: 0,
    stagger: {
      each: 0.025,
      from: "start",
    },
  });
  if (words2) {
    tl.to(
      words2,
      {
        duration: 1,
        ease: "power2.out",
        yPercent: 125,
        stagger: {
          each: 0.01,
          from: "start",
        },
      },
      "<-.05",
    );
  }
  return tl;
}

function VideoButton({ button, styles }) {
  const dispatch = useDispatch();

  const handleClick = () => {
    console.log("Button clicked");
    console.log("Button data:", button);

    const moduleData = {
      type: "mainVideoModalRight",
      src: button.href,
      titleSection: "Video",
      title: button.content,
      desc: button.desc,
      author: button.author,
      stylesAdd: button.stylesAdd,
    };

    console.log("Dispatching module data:", moduleData);
    dispatch(setCurrentModule(moduleData));
  };

  return (
    <button
      key={uuidv4()}
      className={styles.link}
      onClick={() => {
        handleClick()
        if (button.analytics) {
          logEvent("Button", "click", button.analytics);
        }
      }}
    >
      <span>{button.content}</span>
      <svg
        width="11"
        height="8"
        viewBox="0 0 11 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10.3536 4.35355C10.5488 4.15829 10.5488 3.84171 10.3536 3.64645L7.17157 0.464466C6.97631 0.269204 6.65973 0.269204 6.46447 0.464466C6.2692 0.659728 6.2692 0.976311 6.46447 1.17157L9.29289 4L6.46447 6.82843C6.2692 7.02369 6.2692 7.34027 6.46447 7.53553C6.65973 7.7308 6.97631 7.7308 7.17157 7.53553L10.3536 4.35355ZM0 4.5L10 4.5V3.5L0 3.5L0 4.5Z"
          fill="#cf2e2e"
        />
      </svg>
    </button>
  );
}

function ModuleButton({ button, styles }) {
  const dispatch = useDispatch();

  const handleClick = () => {
    console.log("Module button clicked");
    console.log("Module:", button.module);
    dispatch(setCurrentModule(button.module));
  };

  return (
    <button
      key={uuidv4()}
      className={styles.link}
      onClick={() => {
        handleClick()
        if (button.analytics) {
          logEvent("Button", "click", button.analytics);
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      <span>{button.content}</span>
      <svg
        width="11"
        height="8"
        viewBox="0 0 11 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10.3536 4.35355C10.5488 4.15829 10.5488 3.84171 10.3536 3.64645L7.17157 0.464466C6.97631 0.269204 6.65973 0.269204 6.46447 0.464466C6.2692 0.659728 6.2692 0.976311 6.46447 1.17157L9.29289 4L6.46447 6.82843C6.2692 7.02369 6.2692 7.34027 6.46447 7.53553C6.65973 7.7308 6.97631 7.7308 7.17157 7.53553L10.3536 4.35355ZM0 4.5L10 4.5V3.5L0 3.5L0 4.5Z"
          fill="#cf2e2e"
        />
      </svg>
    </button>
  );
}

export function displayContentModal(
  priorities,
  texts,
  images,
  styles,
  buttons = null,
  introContent = null,
  title = null,
) {
  let indexText = 0;
  let indexImage = 0;
  let indexButton = 0;
  let indexIntroduction = 0;
  return priorities.map((elt) => {
    if (elt === "text") {
      return (
        <p
          className={styles.text}
          key={uuidv4()}
          dangerouslySetInnerHTML={{ __html: texts[indexText++] }}
        />
      );
    } else if (elt === "image") {
      return images[indexImage].type && images[indexImage].type === "slider" ? (
        <ImageSlider images={images[indexImage]}></ImageSlider>
      ) : (
        <ImageSolo images={images[indexImage]}></ImageSolo>
      );
    } else if (elt === "button" && buttons) {
      const button = buttons[indexButton++];
      if (button.type === "video") {
        return <VideoButton button={button} styles={styles} />;
      } else if (button.type === "module") {
        return <ModuleButton button={button} styles={styles} />;
      } else {
        return (
          <a
            href={button.href}
            key={uuidv4()}
            className={styles.link}
            target="_blank"
            onClick={() => {
              if (button.analytics) {
                logEvent("Button", "click", button.analytics);
              }
            }}
          >
            <span>{button.content}</span>
            <svg
              width="11"
              height="8"
              viewBox="0 0 11 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.3536 4.35355C10.5488 4.15829 10.5488 3.84171 10.3536 3.64645L7.17157 0.464466C6.97631 0.269204 6.65973 0.269204 6.46447 0.464466C6.2692 0.659728 6.2692 0.976311 6.46447 1.17157L9.29289 4L6.46447 6.82843C6.2692 7.02369 6.2692 7.34027 6.46447 7.53553C6.65973 7.7308 6.97631 7.7308 7.17157 7.53553L10.3536 4.35355ZM0 4.5L10 4.5V3.5L0 3.5L0 4.5Z"
                fill="#cf2e2e"
              />
            </svg>
          </a>
        );
      }
    } else if (elt === "intro") {
      return (
        <div className={styles.intro}>
          <h3
            className={styles.title}
            dangerouslySetInnerHTML={{ __html: title }}
          />
          <div className={styles.introContent}>
            <h4
              dangerouslySetInnerHTML={{
                __html: introContent[indexIntroduction][0],
              }}
            />
            <p
              dangerouslySetInnerHTML={{
                __html: introContent[indexIntroduction++][1],
              }}
            />
          </div>
        </div>
      );
    }
  });
}
export function shuffle(array) {
  let currentIndex = array.length;

  // While there remain elements to shuffle...
  while (currentIndex !== 0) {
    // Pick a remaining element...
    let randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }
}

extend({ BentPlaneGeometry });
